import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { gql } from '@apollo/client';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import debounce from 'lodash.debounce';

import { client, getPostByURI } from '../../apollo-client';
import { useAuth0User } from '@hooks/useAuth0User';
import { useAccessToken } from '@hooks/useAccessToken';
import { useIsAppView } from '@hooks/useIsAppView';
import { usePlayerStats } from '@hooks/usePlayerStats';
import { useTeamStats } from '@hooks/useTeamStats';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';
import {
  CURRENT_SEASON,
  MAX_GAMEWEEK,
  MEMBERSHIPS,
  MIN_GW,
  PAID_MEMBERSHIP_LEVELS
} from '@lib/constants';
import {
  getYoastHeadSEO,
  hasSufficientPrivileges,
  isPaidMember
} from '@lib/utils';
import {
  CUSTOM_STAT_TYPE,
  statOptions,
  typeOptions
} from '@lib/page-utils/Opta';
import {
  getDefaultSortKey,
  getStatTypeConfig,
  OPTA_DETAILED_STATS_FROM_SEASON
} from '@lib/page-utils/Opta/columns';
import {
  buildCustomStatsConfig,
  loadStoredCustomStatPaths,
  storeCustomStatPaths
} from '@lib/page-utils/Opta/custom-stats';

import AnimateFootball from '@components/animate-football';
import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';
import Icon from '@components/atoms/icon';
import InputField from '@components/atoms/input';
import RangeSliderField from '@components/fpl-ui/range-slider-field';
import GameweekRangeSlider from '@components/fpl-ui/gameweek-range-slider';
import Select, { MultiSelect, SelectValue } from '@components/atoms/Select';
import CustomStatsModal from '@components/Opta/CustomStatsModal';
import TableBody from '@components/Opta/TableBody';
import TableHeaders from '@components/Opta/TableHeaders';
import Toggle from '@components/toggle';
import WideLayout from '@components/layout/wide';
import { H1 } from '@components/atoms/typography';
import Button from '@components/atoms/button';

const FFHModal = dynamic(() => import('@components/modal'));
const Paywall = dynamic(() => import('@components/paywall'));

// Fallback price-slider bounds (in £m), used only until the real per-season
// range arrives from /league/context. The low bound sits below FPL's cheapest
// tier so an untouched slider never excludes budget players before the context
// has loaded.
const FALLBACK_PRICE_MIN = 3.5;
const FALLBACK_PRICE_MAX = 15;
const DEFAULT_DIFFICULTY_RANGE: [number, number] = [1, 5];
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 500;

// Minimum-minutes threshold for qualifying a player, expressed as a share of
// the minutes available across the selected gameweek range so it scales with
// the slider instead of being a fixed floor that empties narrow ranges. It
// powers the "Hide low-minutes" toggle, which otherwise only dropped players
// on literally zero minutes. Applying it is always the reader's choice: per-90
// extrapolates tiny samples to nonsense, but forcing the qualifier there made
// low-minute players vanish from the table entirely.
const MINUTES_QUALIFIER_FRACTION = 0.3;
const MINUTES_PER_GAMEWEEK = 90;

// Forced-visible horizontal scrollbar for the stats table. macOS overlay
// scrollbars only appear mid-scroll, which left mouse users with no visible
// way to reach the columns clipped off-screen on wide tabs (e.g. FPL).
const TABLE_SCROLLBAR_CLASSES = `
  [scrollbar-width:thin] [scrollbar-color:theme(colors.neutral.300)_transparent]
  [&::-webkit-scrollbar]:h-2.5
  [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-300
  [&::-webkit-scrollbar-thumb]:hover:bg-neutral-400
  [&::-webkit-scrollbar-track]:bg-neutral-100
`;

const venueOptions: SelectValue[] = [
  { value: 'all', label: 'Home and away' },
  { value: 'home', label: 'Home' },
  { value: 'away', label: 'Away' }
];

// Multi-select options: `short` is what the selected chips render, so several
// positions fit inside the 40px filter control.
const positionOptions: (SelectValue & { short: string })[] = [
  { value: 'GK', label: 'Goalkeepers', short: 'GK' },
  { value: 'DEF', label: 'Defenders', short: 'DEF' },
  { value: 'MID', label: 'Midfielders', short: 'MID' },
  { value: 'FWD', label: 'Forwards', short: 'FWD' }
];

const buildSeasonOptions = (): SelectValue[] => {
  const seasons: SelectValue[] = [];
  for (let year = CURRENT_SEASON; year >= CURRENT_SEASON - 4; year--) {
    const suffix = ((year + 1) % 100).toString().padStart(2, '0');
    seasons.push({ value: year, label: `${year}/${suffix}` });
  }
  return seasons;
};

const seasonOptions = buildSeasonOptions();

// seasonOptions is built newest-first, so index 0 is the current season and
// index 1 the season before it.
const CURRENT_SEASON_OPTION_INDEX = 0;
const PREVIOUS_SEASON_OPTION_INDEX = 1;

const aggregationFromRadio = (
  value: string
): 'total' | 'perAppearance' | 'perStart' | 'per90' => {
  if (value === 'perapp') return 'perAppearance';
  if (value === 'perstart') return 'perStart';
  if (value === 'per90') return 'per90';
  return 'total';
};

/**
 * Inline segmented control sized to match the Select dropdowns on the same
 * row. The shared SegmentedControl atom paints at ~48px tall on desktop,
 * which is visibly heavier than react-select's ~38px control; this variant
 * keeps the brand colours but trims the padding and font size so every
 * filter in the row reads at the same height.
 */
type CompactSegmentedControlProps = {
  value: string;
  onChange: (next: string) => void;
  options: { value: string; label: string }[];
  dataCyPrefix?: string;
};

const CompactSegmentedControl = ({
  value,
  onChange,
  options,
  dataCyPrefix
}: CompactSegmentedControlProps) => (
  <div className="inline-flex items-center rounded-lg bg-primary-100/70 p-0.5">
    {options.map((option) => {
      const isActive = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          data-cy={dataCyPrefix ? `${dataCyPrefix}-${option.value}` : undefined}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors whitespace-nowrap ${
            isActive
              ? 'bg-white text-neutral-800 shadow-sm'
              : 'text-neutral-600 hover:text-neutral-800'
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

type OptaProps = {
  page?: {
    seo?: {
      fullHead?: string;
    };
  };
};

const Opta = ({ page }: OptaProps) => {
  const router = useRouter();
  const { isAppView } = useIsAppView();
  const { user } = useAuth0User();
  const { accessToken, accessTokenIsFetching } = useAccessToken();

  const isOptaStatsPageClosed = usePostHogFeatureFlag(
    PostHogFeatureFlags.optaStatsPageIsClosed,
    false
  );

  const yoastHead = getYoastHeadSEO(page?.seo?.fullHead ?? '', router.pathname);

  const hasPaidMembership = isPaidMember(user);
  const hasSufficientMembershipLevel = hasSufficientPrivileges(
    user,
    PAID_MEMBERSHIP_LEVELS[MEMBERSHIPS.STANDARD_MONTHLY_ID].LEVEL
  );
  const isAuthorisedToViewRestrictedPageContent =
    hasPaidMembership && hasSufficientMembershipLevel;
  const restrictContent = !isAuthorisedToViewRestrictedPageContent;

  const [paywallIsOpen, setPaywallIsOpen] = useState(false);
  const openPaywall = useCallback(() => setPaywallIsOpen(true), []);
  const guardPaidAction = useCallback(
    <T,>(action: () => T): T | undefined => {
      if (restrictContent) {
        setPaywallIsOpen(true);
        return undefined;
      }
      return action();
    },
    [restrictContent]
  );

  const [format, setFormat] = useState<SelectValue>(typeOptions[0]);
  const [statType, setStatType] = useState<SelectValue>(statOptions[0]);
  const [aggregationToken, setAggregationToken] = useState<string>('total');
  const [venue, setVenue] = useState<SelectValue>(venueOptions[0]);
  // null until the reader picks a season, so the default can follow the
  // league context resolved below.
  const [selectedSeason, setSelectedSeason] = useState<SelectValue | null>(
    null
  );
  // An empty selection means "no position filter" (the control shows its
  // "All positions" placeholder); the MultiSelect also reports an emptied or
  // select-all state as every option, so both shapes mean "all".
  const [selectedPositions, setSelectedPositions] = useState<SelectValue[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // null means the user has not moved the price slider, so the full per-season
  // range applies and no price filter is sent.
  const [priceRange, setPriceRange] = useState<[number, number] | null>(null);
  const [gameweekRange, setGameweekRange] = useState<[number, number]>([
    MIN_GW,
    MAX_GAMEWEEK
  ]);
  const [difficultyRange, setDifficultyRange] = useState<[number, number]>(
    DEFAULT_DIFFICULTY_RANGE
  );
  // Off by default so the list shows every player. When it defaulted on, real
  // budget and squad players (below the minutes qualifier) were silently hidden
  // and looked missing.
  const [hideLowMinutes, setHideLowMinutes] = useState(false);
  const [perMillion, setPerMillion] = useState(false);
  // Ticked player ids for the selected-players filter. Player ids are minted
  // per season, so a season change clears the selection rather than carrying
  // ids that no longer resolve.
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]);
  const [isShowingOnlySelected, setIsShowingOnlySelected] = useState(false);
  // Loaded from localStorage after mount; reading it during render would
  // desynchronise the server and client markup.
  const [customStatPaths, setCustomStatPaths] = useState<string[]>([]);
  const [customStatsModalIsOpen, setCustomStatsModalIsOpen] = useState(false);

  useEffect(() => {
    setCustomStatPaths(loadStoredCustomStatPaths());
  }, []);

  const { context: leagueContext, isLoading: isLeagueContextLoading } =
    useLeagueContext();
  // Pre-season the current season has no stats rows yet, so an untouched
  // season select falls back to the previous season instead of opening on an
  // empty table.
  const isPreSeason = leagueContext?.seasonStatus === 'PRE_SEASON';
  const season =
    selectedSeason ??
    seasonOptions[
      isPreSeason ? PREVIOUS_SEASON_OPTION_INDEX : CURRENT_SEASON_OPTION_INDEX
    ];

  const isCustomStatType = statType.value === CUSTOM_STAT_TYPE;
  // Undefined config (custom tab with nothing selected, or custom on teams)
  // keeps the queries disabled and the page shows the picker prompt instead.
  const config = useMemo(() => {
    if (isCustomStatType) {
      return format.value === 'players'
        ? buildCustomStatsConfig(customStatPaths)
        : undefined;
    }
    return getStatTypeConfig(
      format.value as 'players' | 'teams',
      statType.value as string
    );
  }, [format.value, statType.value, isCustomStatType, customStatPaths]);

  const hasDetailedStats =
    Number(season.value) >= OPTA_DETAILED_STATS_FROM_SEASON;
  const detailedStatsSeasonLabel = `${OPTA_DETAILED_STATS_FROM_SEASON}/${(
    (OPTA_DETAILED_STATS_FROM_SEASON + 1) %
    100
  )
    .toString()
    .padStart(2, '0')}`;

  const [sortBy, setSortBy] = useState<string>(
    getDefaultSortKey(config, hasDetailedStats)
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    setSortBy(getDefaultSortKey(config, hasDetailedStats));
    setSortDirection('desc');
  }, [config, hasDetailedStats]);

  const seasonValue = season.value;
  useEffect(() => {
    setSelectedPlayerIds([]);
    setIsShowingOnlySelected(false);
  }, [seasonValue]);

  const handleCustomStatsSave = (paths: string[]) => {
    setCustomStatPaths(paths);
    storeCustomStatPaths(paths);
    setCustomStatsModalIsOpen(false);
  };

  const togglePlayerSelection = useCallback(
    (playerId: string) => {
      guardPaidAction(() =>
        setSelectedPlayerIds((current) =>
          current.includes(playerId)
            ? current.filter((id) => id !== playerId)
            : [...current, playerId]
        )
      );
    },
    [guardPaidAction]
  );

  const debouncedSearchUpdate = useMemo(
    () =>
      debounce((next: string) => {
        setDebouncedSearch(next);
      }, SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(
    () => () => debouncedSearchUpdate.cancel(),
    [debouncedSearchUpdate]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (restrictContent) {
      setPaywallIsOpen(true);
      return;
    }
    const next = event.target.value;
    setSearchInput(next);
    debouncedSearchUpdate(next);
  };

  const isWaitingForAuthToken =
    isAuthorisedToViewRestrictedPageContent &&
    (accessTokenIsFetching || !accessToken);

  const isPlayers = format.value === 'players';
  const aggregation = aggregationFromRadio(aggregationToken);

  // An untouched difficulty slider means "don't filter by difficulty". Sending
  // the full default range still applies a difficulty filter on the backend,
  // which inner-joins against team strengths and silently drops every row for
  // seasons without team-strength data (all seasons before the current one).
  // Omit the bounds entirely unless the user has actually narrowed the range.
  const isDefaultDifficultyRange =
    difficultyRange[0] === DEFAULT_DIFFICULTY_RANGE[0] &&
    difficultyRange[1] === DEFAULT_DIFFICULTY_RANGE[1];

  const selectedGameweekCount = gameweekRange[1] - gameweekRange[0] + 1;
  const minutesQualifier = Math.round(
    MINUTES_QUALIFIER_FRACTION * selectedGameweekCount * MINUTES_PER_GAMEWEEK
  );
  // Only the toggle applies the qualifier. Per-90 must not force it: doing so
  // removed every low-minute player from the table the moment per-90 was
  // selected, rather than leaving the reader to opt in via the toggle.
  const playerMinMinutes = hideLowMinutes ? minutesQualifier : undefined;

  const priceBounds = {
    min: leagueContext?.playerPriceRange?.min ?? FALLBACK_PRICE_MIN,
    max: leagueContext?.playerPriceRange?.max ?? FALLBACK_PRICE_MAX
  };
  const clampPrice = (value: number): number =>
    Math.min(Math.max(value, priceBounds.min), priceBounds.max);
  // Clamp any selection to the live bounds. A range chosen against the fallback
  // bounds (before /league/context loaded) must never fall outside, or invert
  // within, the real per-season range once it arrives.
  const effectivePriceRange: [number, number] = priceRange
    ? [
        clampPrice(Math.min(priceRange[0], priceRange[1])),
        clampPrice(Math.max(priceRange[0], priceRange[1]))
      ]
    : [priceBounds.min, priceBounds.max];
  // An untouched slider, or one dragged back to span the full range, means "no
  // price filter". Sending the bounds as minPrice/maxPrice is what the old
  // hardcoded £4.0m default did, and it silently dropped every player priced
  // below it.
  const isDefaultPriceRange =
    priceRange === null ||
    (effectivePriceRange[0] <= priceBounds.min &&
      effectivePriceRange[1] >= priceBounds.max);

  const sharedQueryOptions = {
    accessToken: accessToken ?? undefined,
    fields: config?.fields ?? 'fpl',
    aggregation,
    venue: venue.value as 'all' | 'home' | 'away',
    season: Number(season.value),
    minGameweek: gameweekRange[0],
    maxGameweek: gameweekRange[1],
    minDifficulty: isDefaultDifficultyRange ? undefined : difficultyRange[0],
    maxDifficulty: isDefaultDifficultyRange ? undefined : difficultyRange[1],
    sortBy,
    sortDirection,
    limit: PAGE_SIZE,
    // Waiting on the league context stops the default-season query firing
    // before it is known whether the current season has data; without it the
    // page would fetch the (empty) current season and immediately refetch the
    // previous one pre-season.
    enabled:
      Boolean(config) &&
      !isWaitingForAuthToken &&
      !isLeagueContextLoading &&
      !isOptaStatsPageClosed
  };

  const isEveryPositionSelected =
    selectedPositions.length === 0 ||
    selectedPositions.length === positionOptions.length;

  const playerQuery = usePlayerStats({
    ...sharedQueryOptions,
    ...(!isEveryPositionSelected && {
      position: selectedPositions
        .map((option) => String(option.value))
        .join(',')
    }),
    ...(isShowingOnlySelected &&
      selectedPlayerIds.length > 0 && { ids: selectedPlayerIds.join(',') }),
    minPrice: isDefaultPriceRange ? undefined : effectivePriceRange[0],
    maxPrice: isDefaultPriceRange ? undefined : effectivePriceRange[1],
    search: debouncedSearch,
    perMillion,
    minMinutes: playerMinMinutes,
    enabled: sharedQueryOptions.enabled && isPlayers
  });

  const teamAggregation: 'total' | 'perAppearance' | 'per90' =
    aggregation === 'perStart' ? 'total' : aggregation;
  const teamQuery = useTeamStats({
    ...sharedQueryOptions,
    aggregation: teamAggregation,
    enabled: sharedQueryOptions.enabled && !isPlayers
  });

  const activeQuery = isPlayers ? playerQuery : teamQuery;
  const rows = useMemo(
    () => (activeQuery.data?.pages ?? []).flatMap((page) => page.data),
    [activeQuery.data]
  );
  const hasMore = activeQuery.hasNextPage ?? false;
  const isRefetching =
    activeQuery.isFetching &&
    !activeQuery.isLoading &&
    !activeQuery.isFetchingNextPage;

  const setSort = (next: string) => {
    if (next === sortBy) {
      setSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortBy(next);
    setSortDirection('desc');
  };

  if (isOptaStatsPageClosed) {
    return (
      <>
        <Head>{yoastHead}</Head>
        <WideLayout>
          <div className="bg-gray-50 px-2 pt-2 lg:pt-8 pb-10 sm:px-4">
            <EndOfSeasonHoldingMessage
              pageTitle="The OPTA stats page"
              isAppView={isAppView}
            />
          </div>
        </WideLayout>
      </>
    );
  }

  const visibleAggregationOptions = isPlayers
    ? [
        { value: 'total', label: 'Total' },
        { value: 'perapp', label: 'Per app' },
        { value: 'perstart', label: 'Per start' },
        { value: 'per90', label: 'Per 90' }
      ]
    : [
        { value: 'total', label: 'Total' },
        { value: 'perapp', label: 'Per app' },
        { value: 'per90', label: 'Per 90' }
      ];

  return (
    <div className="bg-neutral-100 min-h-screen">
      <Head>{yoastHead}</Head>
      <WideLayout>
        <div className="mx-auto max-w-7xl px-3 py-6 sm:px-4 lg:px-6 lg:py-10">
          <FFHModal
            open={paywallIsOpen}
            handleClose={() => setPaywallIsOpen(false)}
          >
            <Paywall variant="modal" title="Unlock full OPTA stats" />
          </FFHModal>

          <CustomStatsModal
            open={customStatsModalIsOpen}
            selectedPaths={customStatPaths}
            onSave={handleCustomStatsSave}
            onClose={() => setCustomStatsModalIsOpen(false)}
          />

          <header className="mb-6">
            <H1
              className="text-2xl font-bold tracking-tight text-neutral-800"
              data-cy="opta-stats-title"
            >
              OPTA stats
            </H1>
          </header>

          {/* This card must not clip overflow: the Select menus render
              inline (the atom's portal target doesn't exist in this app),
              so an overflow-hidden card cuts the bottom off open dropdowns
              (the position menu lost its last option, Forwards). */}
          <div className="mb-6 rounded-2xl bg-white shadow-sm ring-1 ring-neutral-200/70">
            <div className="flex items-center gap-1 overflow-x-auto border-b border-neutral-200 px-3 py-2.5 sm:px-4">
              {statOptions
                .filter(
                  (option) => isPlayers || option.value !== CUSTOM_STAT_TYPE
                )
                .map((option) => {
                  const isActive = statType.value === option.value;
                  const selectStatType = () => {
                    if (option.value !== CUSTOM_STAT_TYPE) {
                      setStatType(option as SelectValue);
                      return;
                    }
                    guardPaidAction(() => {
                      setStatType(option as SelectValue);
                      if (customStatPaths.length === 0) {
                        setCustomStatsModalIsOpen(true);
                      }
                    });
                  };
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={selectStatType}
                      data-cy={`opta-stat-tab-${option.value}`}
                      aria-pressed={isActive}
                      className={`
                      relative shrink-0 px-3 py-1.5 text-xs font-semibold tracking-tight
                      rounded-lg transition-colors
                      ${
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                      }
                    `}
                    >
                      {option.label}
                    </button>
                  );
                })}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 px-3 py-3 sm:px-4">
              <CompactSegmentedControl
                value={format.value as string}
                onChange={(next) => {
                  setFormat(
                    (typeOptions.find((option) => option.value === next) ??
                      typeOptions[0]) as SelectValue
                  );
                  if (next === 'teams' && aggregationToken === 'perstart') {
                    setAggregationToken('total');
                  }
                  if (next === 'teams' && isCustomStatType) {
                    setStatType(statOptions[0] as SelectValue);
                  }
                }}
                options={typeOptions.map((option) => ({
                  value: String(option.value),
                  label: String(option.label)
                }))}
                dataCyPrefix="opta-format"
              />

              <CompactSegmentedControl
                value={aggregationToken}
                onChange={(next) => setAggregationToken(next)}
                options={visibleAggregationOptions}
                dataCyPrefix="opta-aggregation"
              />
            </div>

            {/* The controls are pinned to one 40px height via the react-select
                control class and the search input's own class. A blanket
                [&_input] height rule must not come back here: it also hits
                react-select's internal dummy input, which inflates every
                dropdown above 40px and leaves the search box looking short. */}
            <div
              className="grid grid-cols-1 gap-2 px-3 py-3 sm:px-4 sm:grid-cols-2 lg:grid-cols-4
                         [&_.label-wrapper]:hidden [&_label]:hidden [&_.mt-2]:!mt-0
                         [&_.ffh-select\_\_control]:!h-[40px] [&_.ffh-select\_\_control]:!min-h-[40px]"
            >
              {/* Search leads the row: it is the filter people reach for
                  first. The icon sits over the input, which is padded to
                  clear it. */}
              <div className={`relative ${isPlayers ? '' : 'sm:col-span-2'}`}>
                <Icon
                  name="Search"
                  className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-neutral-600"
                />
                <InputField
                  name="name"
                  type="text"
                  className="!h-[40px] !pl-9"
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder="Search players or teams"
                />
              </div>
              {isPlayers && (
                <MultiSelect
                  options={positionOptions}
                  value={selectedPositions}
                  placeholder="All positions"
                  onChange={(next: SelectValue[]) =>
                    guardPaidAction(() => setSelectedPositions(next))
                  }
                />
              )}
              <Select
                name="venue"
                options={venueOptions}
                value={venue}
                onChange={(next) =>
                  guardPaidAction(() => setVenue(next as SelectValue))
                }
              />
              <Select
                name="season"
                options={seasonOptions}
                value={season}
                onChange={(next) =>
                  guardPaidAction(() => setSelectedSeason(next as SelectValue))
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-5 border-t border-neutral-100 px-3 py-4 sm:px-4 lg:grid-cols-3">
              {isPlayers && (
                <RangeSliderField
                  label="Price"
                  name="price-range"
                  onCommit={(next) =>
                    guardPaidAction(() => setPriceRange(next))
                  }
                  min={priceBounds.min}
                  max={priceBounds.max}
                  step={0.1}
                  value={effectivePriceRange}
                  formatLabel={(price) => `£${price.toFixed(1)}m`}
                  // Fits £15.0m.
                  pillMinWidthClassName="!min-w-[62px]"
                />
              )}
              <GameweekRangeSlider
                name="gameweek-slider"
                onCommit={(next) =>
                  guardPaidAction(() => setGameweekRange(next))
                }
                min={MIN_GW}
                max={MAX_GAMEWEEK}
                value={gameweekRange}
              />
              <RangeSliderField
                label="Fixture difficulty"
                name="difficulty-slider"
                onCommit={(next) =>
                  guardPaidAction(() => setDifficultyRange(next))
                }
                min={1}
                max={5}
                step={1}
                value={difficultyRange}
                pillMinWidthClassName="!min-w-[32px]"
              />
            </div>

            {isPlayers && (
              <div className="flex flex-wrap items-center gap-4 border-t border-neutral-100 px-3 py-3 sm:px-4">
                <Toggle
                  name="hidelow"
                  onChange={() =>
                    guardPaidAction(() => setHideLowMinutes((value) => !value))
                  }
                  text="Hide low-minutes"
                  checked={hideLowMinutes}
                />
                <Toggle
                  name="perMillion"
                  onChange={() =>
                    guardPaidAction(() => setPerMillion((value) => !value))
                  }
                  text="Per million"
                  checked={perMillion}
                />
                {selectedPlayerIds.length > 0 && (
                  <Toggle
                    name="onlySelected"
                    onChange={() =>
                      guardPaidAction(() =>
                        setIsShowingOnlySelected((value) => !value)
                      )
                    }
                    text={`Only selected (${selectedPlayerIds.length})`}
                    checked={isShowingOnlySelected}
                  />
                )}
                {isCustomStatType && (
                  <Button
                    variant="secondary"
                    size="small"
                    data-cy="opta-edit-custom-stats"
                    onClick={() => setCustomStatsModalIsOpen(true)}
                  >
                    Edit custom stats
                  </Button>
                )}
              </div>
            )}
          </div>

          {!hasDetailedStats && (
            <div
              data-cy="opta-historical-stats-notice"
              className="mb-6 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-amber-200"
            >
              Detailed OPTA stats (shots, passing, involvement, set pieces)
              aren&apos;t available before the {detailedStatsSeasonLabel}{' '}
              season. Expected goals, assists and FPL metrics are shown, and the
              table is sorted by an available column.
            </div>
          )}

          {isCustomStatType && !config ? (
            <div className="rounded-2xl bg-white p-12 text-center shadow-sm ring-1 ring-neutral-200/70">
              <p className="text-sm text-neutral-600">
                Pick the stats you want in your custom table.
              </p>
              <div className="mt-4">
                <Button
                  variant="primary"
                  data-cy="opta-select-custom-stats"
                  onClick={() => setCustomStatsModalIsOpen(true)}
                >
                  Select custom stats
                </Button>
              </div>
            </div>
          ) : activeQuery.isLoading ? (
            <div className="rounded-2xl bg-white p-16 text-center shadow-sm ring-1 ring-neutral-200/70">
              <AnimateFootball
                verbs={['Fetching stats', 'Crunching numbers']}
                height={120}
              />
            </div>
          ) : activeQuery.isError ? (
            <div className="rounded-2xl bg-white p-12 text-center text-sm text-red-600 shadow-sm ring-1 ring-red-200">
              Something went wrong loading stats. Please try again.
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl bg-white p-12 text-center text-sm text-neutral-500 shadow-sm ring-1 ring-neutral-200/70">
              No {isPlayers ? 'players' : 'teams'} match these filters.
            </div>
          ) : (
            <div
              className={`relative overflow-x-auto rounded-2xl bg-white shadow-sm ring-1 ring-neutral-200/70 transition-opacity ${TABLE_SCROLLBAR_CLASSES} ${
                isRefetching ? 'opacity-60 pointer-events-none' : ''
              }`}
              aria-busy={isRefetching}
            >
              {isRefetching && (
                <div
                  role="status"
                  aria-live="polite"
                  className="absolute top-3 right-3 z-30 rounded-full bg-primary-600 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white shadow"
                >
                  Updating
                </div>
              )}
              <table className="min-w-full">
                <TableHeaders
                  format={isPlayers ? 'players' : 'teams'}
                  groups={config?.groups ?? []}
                  sort={sortBy}
                  sortDirection={sortDirection}
                  setSort={setSort}
                />
                <TableBody
                  format={isPlayers ? 'players' : 'teams'}
                  rows={rows}
                  groups={config?.groups ?? []}
                  aggregation={aggregation}
                  perMillion={perMillion}
                  restrictContent={restrictContent}
                  openPaywall={openPaywall}
                  {...(isPlayers && {
                    selectedPlayerIds,
                    onTogglePlayerSelection: togglePlayerSelection
                  })}
                />
              </table>
            </div>
          )}

          {hasMore && (
            <div className="text-center mt-6">
              <Button
                variant="primary"
                onClick={() => activeQuery.fetchNextPage()}
                disabled={activeQuery.isFetchingNextPage}
              >
                {activeQuery.isFetchingNextPage ? 'Loading...' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      </WideLayout>
    </div>
  );
};

export const getStaticProps = async () => {
  const { data } = await client.query({
    query: gql`
      ${getPostByURI('/opta')}
    `
  });

  return { props: { page: data.postBy } };
};

export default Opta;
